import * as React from 'react';

import {
  Badge,
  Button,
  Col,
  FloatingToolbar,
  Menu,
  MultiSelectFilter,
  Pagination,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  SearchField,
  TinyTable,
  Tooltip,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideChevronDown,
  LucideEllipsis,
  LucideMail,
  LucidePhone,
  LucidePlusCircle,
  LucideSend,
  LucideSettings,
  LucideUserMinus,
  LucideUser,
  LucideX,
} from 'lucide-react';

import { ConfirmationDialog } from '../_shared/ConfirmationDialog';

import { RoleUserRecord } from './_graphql/useRoleUsersQuery';
import { useRoleUsersBulkActions } from './_hooks/useRoleUsersBulkActions';
import { useRoleUsersTableFilters } from './_hooks/useRoleUsersTableFilters';

import {
  FacilitiesCell,
  UserStatusBadge,
} from '~/modules/settings/_components/_shared';
import { $openAddUsersDialog } from '~/modules/settings/_state/addUsersToRoleDialog.state';
import { $currentRole } from '~/modules/settings/_state/roleForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { PermissionGate } from '~/shared/components/PermissionGate';
import { usePermission } from '~/shared/hooks/usePermission';

/**
 * Menu component for role user row actions
 */
type RoleUserRowMenuProps = {
  user: RoleUserRecord;
  onViewUserSettings: () => void;
  onResendInvite: () => void;
  onRemoveFromRole: () => void;
};

const RoleUserRowMenu = ({
  user,
  onViewUserSettings,
  onResendInvite,
  onRemoveFromRole,
}: RoleUserRowMenuProps) => {
  const t = useTranslations();
  const { allowed: canUpdateRole } = usePermission('roleUpdate');

  const menuItems = React.useMemo(() => {
    const items: Array<{ label: React.ReactNode; onAction: () => void }> = [];

    if (user.status === 'invite_sent') {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <LucideSend className="size-4" />
            {t.teamAndPermissions.users.actions.resendInvite}
          </div>
        ),
        onAction: onResendInvite,
      });
    }

    items.push({
      label: (
        <div className="flex items-center gap-2">
          <LucideSettings className="size-4" />
          {t.teamAndPermissions.roles.details.users.viewUserSettings}
        </div>
      ),
      onAction: onViewUserSettings,
    });

    if (canUpdateRole) {
      items.push({
        label: (
          <div className="flex items-center gap-2 text-failure">
            <LucideUserMinus className="size-4" />
            {t.teamAndPermissions.roles.details.users.removeFromRole}
          </div>
        ),
        onAction: onRemoveFromRole,
      });
    }

    return items;
  }, [
    user.status,
    t,
    canUpdateRole,
    onViewUserSettings,
    onResendInvite,
    onRemoveFromRole,
  ]);

  return (
    <Menu
      trigger={
        <Button
          size="icon"
          icon={<LucideEllipsis />}
          variant="outline"
          color="neutral"
        />
      }
      items={menuItems}
      menuClassName="@RoleUserRowMenu"
    />
  );
};

export const RoleUsersTab = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const currentRole = useAtomValue($currentRole);
  const openAddUsersDialog = useSetAtom($openAddUsersDialog);

  const filters = useRoleUsersTableFilters(currentRole?.id ?? '');
  const bulk = useRoleUsersBulkActions(filters.allUsers);

  const handleViewUserSettings = (user: RoleUserRecord) => {
    setLocation(`/team-and-permissions/users/${user.id}` as never);
  };

  const handleAddUser = () => {
    if (currentRole) {
      openAddUsersDialog({
        roleId: currentRole.id,
        roleName: currentRole.name,
        existingUserIds: new Set(filters.allUsers.map((u) => u.id)),
      });
    }
  };

  if (!currentRole) {
    return null;
  }

  return (
    <Col className="@RoleUsersTab h-full gap-4 p-5">
      <Row gap2>
        <SearchField
          value={filters.searchTerm}
          onChange={filters.handleSearchChange}
          placeholder={t.teamAndPermissions.search.placeholder}
          autoFocus
        />
        <PopoverTrigger>
          <Button variant="outline" color="neutral" className="gap-1.5">
            {t.teamAndPermissions.users.filters.allFacilities}
            {filters.facilityFilter.length > 0 && (
              <Badge color="primary" size="small">
                {filters.facilityFilter.length}
              </Badge>
            )}
            <LucideChevronDown className="size-4 text-neutral/40" />
          </Button>
          <Popover placement="bottom start">
            <PopoverDialog>
              {({ close }: { close: () => void }) => (
                <MultiSelectFilter
                  className="min-w-[280px]"
                  entries={filters.facilities}
                  selectedEntries={filters.selectedFacilities}
                  selectedKeys={filters.selectedFacilityKeys}
                  onSelectionChange={filters.handleFacilityFilterChange}
                  getEntryKey={(f) => f.id}
                  getEntryLabel={(f) => f.name}
                  onReset={filters.handleFacilityFilterReset}
                  onDismiss={close}
                  searchPlaceholder={t.teamAndPermissions.search.placeholder}
                />
              )}
            </PopoverDialog>
          </Popover>
        </PopoverTrigger>
        <PopoverTrigger>
          <Button variant="outline" color="neutral" className="gap-1.5">
            {t.teamAndPermissions.users.filters.allRoles}
            {filters.rolesFilter.length > 0 && (
              <Badge color="primary" size="small">
                {filters.rolesFilter.length}
              </Badge>
            )}
            <LucideChevronDown className="size-4 text-neutral/40" />
          </Button>
          <Popover placement="bottom start">
            <PopoverDialog>
              {({ close }: { close: () => void }) => (
                <MultiSelectFilter
                  className="min-w-[280px]"
                  entries={filters.roles}
                  selectedEntries={filters.selectedRoles}
                  selectedKeys={filters.selectedRoleKeys}
                  onSelectionChange={filters.handleRolesFilterChange}
                  getEntryKey={(r) => r.id}
                  getEntryLabel={(r) => r.name}
                  onReset={filters.handleRolesFilterReset}
                  onDismiss={close}
                  searchPlaceholder={t.teamAndPermissions.search.placeholder}
                />
              )}
            </PopoverDialog>
          </Popover>
        </PopoverTrigger>
        <PermissionGate action="roleUpdate">
          {filters.allUsersAdded ? (
            <Tooltip
              tip={t.teamAndPermissions.actions.allUsersAdded}
              className="ml-auto"
            >
              <div className="h-input inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm text-abs-white capitalize opacity-20">
                <LucidePlusCircle size={16} className="shrink-0" />
                {t.teamAndPermissions.actions.addUser}
              </div>
            </Tooltip>
          ) : (
            <Button
              className="ml-auto"
              icon={<LucidePlusCircle />}
              onPress={handleAddUser}
            >
              {t.teamAndPermissions.actions.addUser}
            </Button>
          )}
        </PermissionGate>
      </Row>

      <TinyTable<RoleUserRecord>
        rowKey={(row) => row.id}
        selectable
        selectedKeys={bulk.selectedKeys}
        onSelectionChange={bulk.handleSelectionChange}
        isLoading={filters.isLoading}
        error={filters.error ?? undefined}
        sorting={filters.sorting}
        onSortChange={filters.handleSortChange}
        onRowClick={(row) => handleViewUserSettings(row)}
        columns={[
          {
            key: 'name',
            label: t.teamAndPermissions.users.table.columns.name,
            icon: LucideUser,
            sortable: true,
            cell: (row) => (
              <Col className="gap-0.5">
                <span>{row.name}</span>
                {row.roles.length > 0 && (
                  <span className="text-xs text-neutral/50">
                    {row.roles.join(', ')}
                  </span>
                )}
              </Col>
            ),
          },
          {
            key: 'contactInfo',
            label: t.teamAndPermissions.users.table.columns.contactInfo,
            icon: LucideMail,
            sortable: true,
            cell: (row) => (
              <Col className="gap-0.5">
                <Row className="items-center gap-1.5">
                  <LucideMail className="size-3 text-neutral/50" />
                  <span className="truncate">{row.email || '-'}</span>
                </Row>
                {row.phoneNumber && (
                  <Row className="items-center gap-1.5">
                    <LucidePhone className="size-3 text-neutral/50" />
                    <span>{row.phoneNumber}</span>
                  </Row>
                )}
              </Col>
            ),
          },
          {
            key: 'facilities',
            label: t.teamAndPermissions.users.table.columns.facilities,
            width: 180,
            sortable: true,
            cell: (row) => <FacilitiesCell facilities={row.facilities} />,
          },
          {
            key: 'status',
            label: t.teamAndPermissions.users.table.columns.status,
            width: 100,
            sortable: true,
            cell: (row) => <UserStatusBadge status={row.status} />,
          },
          {
            key: 'actions',
            label: '',
            width: 60,
            preventRowClick: true,
            cell: (row) => (
              <RoleUserRowMenu
                user={row}
                onViewUserSettings={() => handleViewUserSettings(row)}
                onResendInvite={() => bulk.handleResendInvite(row)}
                onRemoveFromRole={() => bulk.handleRemoveFromRole(row)}
              />
            ),
          },
        ]}
        data={filters.paginatedUsers}
      />

      <FloatingToolbar visible={bulk.selectedUserIds.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {bulk.selectedUserIds.length}
            </Badge>{' '}
            {t.teamAndPermissions.roles.details.users.bulk.selected}
          </div>
          <div className="flex flex-row items-center gap-2">
            <PermissionGate action="roleUpdate">
              <Button
                variant="outline"
                size="small"
                icon={<LucideUserMinus className="size-4" />}
                onPress={bulk.handleBulkRemoveFromRole}
                isDisabled={bulk.isBulkActionRunning}
              >
                {t.teamAndPermissions.roles.details.users.bulk.removeFromRole} (
                {bulk.selectedUserIds.length})
              </Button>
            </PermissionGate>
            {bulk.inviteSentUserIds.length > 0 && (
              <Button
                variant="outline"
                size="small"
                icon={<LucideSend className="size-4" />}
                onPress={bulk.handleBulkResendInvite}
                isDisabled={bulk.isBulkActionRunning}
              >
                {t.teamAndPermissions.roles.details.users.bulk.resendInvite} (
                {bulk.inviteSentUserIds.length})
              </Button>
            )}
            <LucideX
              className="size-4 cursor-pointer text-neutral/80"
              onClick={bulk.handleClearSelection}
            />
          </div>
        </div>
      </FloatingToolbar>

      {filters.total > 0 && (
        <Row className="justify-end">
          <Pagination
            limit={filters.limit}
            offset={filters.offset}
            total={filters.total}
            onChange={filters.handlePaginationChange}
          />
        </Row>
      )}

      <ConfirmationDialog
        isOpen={bulk.pendingBulkRemove}
        onOpenChange={bulk.handleBulkRemoveDialogOpenChange}
        title={t.teamAndPermissions.roles.details.users.bulk.bulkRemoveDialog.title(
          bulk.selectedUserIds.length,
        )}
        description={t.teamAndPermissions.roles.details.users.bulk.bulkRemoveDialog.description(
          bulk.selectedUserIds.length,
        )}
        confirmLabel={
          t.teamAndPermissions.roles.details.users.bulk.bulkRemoveDialog.confirm
        }
        cancelLabel={
          t.teamAndPermissions.roles.details.users.bulk.bulkRemoveDialog.cancel
        }
        onConfirm={bulk.handleConfirmBulkRemove}
        onCancel={() => bulk.handleBulkRemoveDialogOpenChange(false)}
        isLoading={bulk.isBulkActionRunning}
      />

      <ConfirmationDialog
        isOpen={bulk.userToRemove !== null}
        onOpenChange={bulk.handleRemoveDialogOpenChange}
        title={t.teamAndPermissions.roles.details.users.removeFromRoleDialog.title(
          bulk.userToRemove?.name ?? '',
        )}
        description={t.teamAndPermissions.roles.details.users.removeFromRoleDialog.description(
          bulk.userToRemove?.name ?? '',
        )}
        confirmLabel={
          t.teamAndPermissions.roles.details.users.removeFromRoleDialog.confirm
        }
        cancelLabel={
          t.teamAndPermissions.roles.details.users.removeFromRoleDialog.cancel
        }
        onConfirm={bulk.handleRemoveConfirm}
        onCancel={() => bulk.handleRemoveDialogOpenChange(false)}
        isLoading={bulk.isRemovePending}
      />
    </Col>
  );
};
